import React, { useState, useEffect, useCallback } from 'react';
import { IoCallOutline, IoTimeOutline, IoShieldCheckmarkOutline, IoChevronDown, IoChevronUp } from 'react-icons/io5';

const RANGES = [['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days']];
const fmtMin = (s) => s ? `${Math.round(s / 60)} min` : '0 min';
const pct = (x) => x == null ? '—' : `${Math.round(x * 100)}%`;

const moodEmoji = (m) => ({ up: '🙂', down: '😟', flat: '😐' }[m?.direction] || '😐');
const arrow = (d) => ({ up: '↑', down: '↓', flat: '→' }[d] || '');

export default function CareInsightsTab({ account, api, token }) {
  const id = account?.id;
  const [range, setRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // call_sid -> transcript|null

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!id || !api || !token) return;
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        fetch(`${api}/api/managed-accounts/${id}/call-analytics?range=${range}`, auth).then(r => r.ok ? r.json() : null),
        fetch(`${api}/api/managed-accounts/${id}/calls?range=${range}`, auth).then(r => r.ok ? r.json() : { calls: [] }),
      ]);
      setAnalytics(a); setCalls(c.calls || []);
    } finally { setLoading(false); }
  }, [id, api, token, range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggle = async (sid) => {
    if (expanded[sid] !== undefined) { setExpanded(e => ({ ...e, [sid]: undefined })); return; }
    setExpanded(e => ({ ...e, [sid]: null }));   // loading
    const r = await fetch(`${api}/api/managed-accounts/${id}/calls/${sid}/transcript`, auth);
    const t = r.ok ? await r.json() : { full_transcript: '(unavailable)' };
    setExpanded(e => ({ ...e, [sid]: t }));
  };

  if (loading) return <div className="care-insights-empty">Loading insights…</div>;
  const e = analytics?.engagement, s = analytics?.safety;
  const hasData = !!(e && s) && ((calls.length > 0) || e.calls_total > 0);

  return (
    <div className="care-insights">
      <div className="care-range">
        {RANGES.map(([v, label]) => (
          <button key={v} className={`care-range-btn ${range === v ? 'active' : ''}`} onClick={() => setRange(v)}>{label}</button>
        ))}
      </div>

      {!hasData && <div className="care-insights-empty">Building insights — check back after a few calls.</div>}

      {hasData && <>
        <div className="care-cards">
          <div className="care-card"><IoCallOutline /><div className="care-card-num">{e.answered} of {e.calls_total}</div><div className="care-card-label">calls answered</div></div>
          <div className="care-card"><IoTimeOutline /><div className="care-card-num">{fmtMin(e.total_talk_s)}</div><div className="care-card-label">time together · {fmtMin(e.avg_duration_s)}/call</div></div>
          <div className="care-card"><IoShieldCheckmarkOutline /><div className="care-card-num">{s.crises_resolved}/{s.crises_total}</div><div className="care-card-label">concerns resolved</div></div>
          <div className="care-card">
            <span className="care-mood-emoji">{moodEmoji(analytics.mood)}</span>
            <div className="care-card-num">{analytics.mood?.current_label || '—'} {arrow(analytics.mood?.direction)}</div>
            <div className="care-card-label">recent mood</div>
          </div>
        </div>

        <Section title="Engagement">
          <Bars data={e.per_week} />
          <div className="care-sub">Answer rate {pct(e.answer_rate)} · avg {Math.round(e.avg_user_turns)} replies/call</div>
        </Section>

        <Section title="Safety & reminders">
          <div className="care-sub">{s.crises_total} concern(s), {s.crises_resolved} resolved
            {s.avg_resolve_hours != null ? ` (avg ${s.avg_resolve_hours.toFixed(1)}h)` : ''} ·
            reminder calls answered {s.reminders_followed}/{s.reminders_total} ({pct(s.adherence_rate)})</div>
        </Section>

        <Section title="Recent calls">
          {calls.map(c => (
            <div key={c.call_sid} className="care-call-row">
              <button className="care-call-head" onClick={() => toggle(c.call_sid)}>
                <span className="care-call-date">{new Date(c.started_at).toLocaleDateString()}</span>
                <span className={`care-badge ${['no-answer', 'busy', 'failed'].includes(c.status) ? 'missed' : 'ok'}`}>
                  {['no-answer', 'busy', 'failed'].includes(c.status) ? 'missed' : (c.duration_s ? `${Math.round(c.duration_s / 60)}m` : 'answered')}
                </span>
                <span className="care-call-sum">{c.summary || 'Call'}</span>
                {expanded[c.call_sid] !== undefined ? <IoChevronUp /> : <IoChevronDown />}
              </button>
              {expanded[c.call_sid] !== undefined && (
                <pre className="care-transcript">
                  {expanded[c.call_sid] === null ? 'Loading…' : (expanded[c.call_sid].full_transcript || '(empty)')}
                </pre>
              )}
            </div>
          ))}
        </Section>

        <Section title="Mood over time"><Sparkline points={analytics.mood?.trend || []} /></Section>

        <Section title="What they talk about">
          <div className="care-chips">
            {(analytics.themes || []).map(t => (
              <span key={t.theme} className="care-chip" style={{ fontSize: 11 + Math.min(6, t.count) }}>{t.theme}</span>
            ))}
            {(!analytics.themes || !analytics.themes.length) && <span className="care-sub">No themes yet.</span>}
          </div>
        </Section>

        <Section title="Life-story moments">
          {(analytics.memory?.highlights || []).map((h, i) => (
            <div key={i} className="care-memory"><span className="care-call-date">{h.call_date}</span> {h.text}</div>
          ))}
          {(!analytics.memory?.highlights?.length) && <span className="care-sub">Moments will appear as they&apos;re shared.</span>}
        </Section>
      </>}
    </div>
  );
}

function Section({ title, children }) {
  return <div className="care-section"><h4 className="care-section-title">{title}</h4>{children}</div>;
}

function Sparkline({ points }) {
  if (!points || points.length < 2) return <div className="care-sub">Not enough calls yet for a trend.</div>;
  const W = 240, H = 50, n = points.length;
  const xs = (i) => (i / (n - 1)) * (W - 8) + 4;
  const ys = (v) => H - 6 - ((v - 1) / 4) * (H - 12);   // mood 1..5
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${xs(i).toFixed(1)} ${ys(p.score).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="care-spark" width="100%" height={H}>
      <path d={d} fill="none" stroke="#bd7b69" strokeWidth="2" />
      {points.map((p, i) => <circle key={i} cx={xs(i)} cy={ys(p.score)} r="2.5" fill="#bd7b69" />)}
    </svg>
  );
}

// Inline SVG bar chart — answered (filled) vs missed (light) per week. No deps.
function Bars({ data }) {
  if (!data || !data.length) return <div className="care-sub">No calls yet.</div>;
  const max = Math.max(1, ...data.map(d => d.answered + d.missed));
  const W = 240, H = 60, bw = Math.max(6, Math.min(28, W / data.length - 6));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="care-bars" width="100%" height={H}>
      {data.map((d, i) => {
        const x = i * (W / data.length) + 4;
        const ah = (d.answered / max) * (H - 8), mh = (d.missed / max) * (H - 8);
        return (
          <g key={d.week}>
            <rect x={x} y={H - ah} width={bw} height={ah} fill="#bd7b69" rx="2" />
            <rect x={x} y={H - ah - mh} width={bw} height={mh} fill="#e7d4cd" rx="2" />
          </g>
        );
      })}
    </svg>
  );
}
